import { autorun } from "mobx"
import { observer } from "mobx-react-lite"
import { useContentEditorStore } from "pages/content/store"
import ContentBlock from "pages/content/store/block"
import Value from "pages/content/store/value"
import { useEffect } from "react"
import { toOne } from "utils/lang"
import { getLink } from "utils/other"
import Collapse from "../collapse"
import ContentInput from "../input"
import MultilineContentInput from "../input/multiline"
import ContentSelect from "../select"

type Props = {
  state: ContentBlock,
  value: Value
}

const BaseBlock = observer(({ state, value }: Props) => {

  const store = useContentEditorStore()
  const link = state.block.type === "array" && getLink(state.block.data.count)
  useEffect(() => {
    if (!link) return
    const block = store.findValue(value => value.contentBlock.blockId === link)
    if (!block) return
    return autorun(() => {
      value.set(block.value)
    })
  }, [ link, state.blockId ])  // eslint-disable-line react-hooks/exhaustive-deps

  if (state.enabled === false) return null

  if (state.block.type === "value") {
    const dataType = state.block.data.dataType || "string"
    if (dataType === "string" && state.block.data.multiline === "true") {
      return <MultilineContentInput 
        tooltip={state.block.data.description} 
        value={value} 
        label={state.block.data.name} 
        placeholder="Пустое значение"/>
    }
    return (
      <ContentInput 
        tooltip={state.block.data.description} 
        value={value} 
        label={state.block.data.name} 
        placeholder="Пустое значение"
        type={dataType}/>
    )
  }

  if (state.block.type === "group") {
    return (
      <Collapse label={state.block.data.name} tooltip={state.block.data.description} >
        { (value.children as Value[]).map((val, chIndex) => (
          <BaseBlock 
            state={val.contentBlock} 
            key={chIndex}
            value={val}
          />
        )) }
      </Collapse>
    )
  }

  if (state.block.type === "array") {
    const isDisabled = !!state.block.data.count
    const startIndex = Value.getStartIndex(state)
    return (
      <Collapse label={state.block.data.name} tooltip={state.block.data.description}>
        <ContentInput 
          value={value} 
          label="Количество" 
          disabled={isDisabled} 
          tooltip={isDisabled? "Кол-во не изменяется напрямую": undefined}
          type="number"
        />
        { (value.children as Value[][]).map((arr, index) => (
          <Collapse key={index} label={toOne(state.block.data.name || "")+" "+(index+startIndex)}>
            { arr.map((val, chIndex) => (
              <BaseBlock 
                state={val.contentBlock} 
                key={chIndex}
                value={val}
              />
            )) }
          </Collapse>
        ))}
      </Collapse>
    )
  }

  if (state.block.type === "selectable") {
    
    if (state.children.length === 0) return null

    return (
      <>
        <ContentSelect 
          label={state.block.data.name} 
          options={state.block.emitters}
          value={value}
          tooltip={state.block.data.description}
        />
        { (value.children as Value[]).map((val, index) => (
          <BaseBlock 
            state={val.contentBlock} 
            key={index}
            value={val}
          />
        ))}
      </>
    )
  }

  return null
  
})

export default BaseBlock